import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import {
  getMe,
  getStoredUser,
  getStoredToken,
  logout as apiLogout,
  setDefaultSite as apiSetDefaultSite,
  setMyDashboardLayout as apiSetDashboardLayout,
  saveTourState as apiSaveTourState,
} from '../services/api.service';
import { clearStoredScope } from '../constants/scope';
import { hasPermission as checkPermission } from '../utils/permissions';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  role_id?: string;
  permissions?: string[];
  /** Favourite site: the scope a fresh session opens on. null = All Sites. */
  default_node_id?: string | null;
  /**
   * My dashboard card layout, shared by the All Sites and per-site boards. null = the default;
   * ABSENT (undefined) = a backend that cannot store one, so the board keeps it in the browser.
   */
  dashboard_layout?: unknown[] | null;
  /** Onboarding tours finished or skipped, keyed by tour id. See src/tours. */
  tour_state?: Record<string, { version: number; status: string; at: string }>;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  logout: () => void;
  setUser: (user: User, token: string) => void;
  /** Persist my favourite site (null clears it). Resolves once the server agrees. */
  setDefaultSite: (nodeId: string | null) => Promise<void>;
  /** Remember my dashboard layout (null = default). Applied at once, saved shortly after. */
  saveDashboardLayout: (layout: unknown[] | null) => void;
  /** Persist that I finished or skipped a tour, so it is not offered again. */
  markTourSeen: (tourId: string, version: number, status: 'completed' | 'skipped') => Promise<void>;
  hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  loading: true,
  logout: () => { },
  setUser: () => { },
  setDefaultSite: async () => { },
  saveDashboardLayout: () => { },
  markTourSeen: async () => { },
  hasPermission: () => false,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<User | null>(getStoredUser());
  const [token, setToken] = useState<string | null>(getStoredToken());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      getMe()
        .then((data) => {
          setUserState(data.user);
          localStorage.setItem('cloud_user', JSON.stringify(data.user));
        })
        .catch(() => {
          apiLogout();
          setUserState(null);
          setToken(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const setUser = (user: User, token: string) => {
    setUserState(user);
    setToken(token);
  };

  /**
   * Server first, then local state — so a star that lights up is one that will
   * still be lit after the next login. Updating optimistically would show a
   * favourite that a failed request never actually saved.
   *
   * The cached copy in localStorage is updated too, because getStoredUser()
   * seeds `user` on the next mount before /auth/me has answered; leaving it
   * stale would make the star flicker off and back on at every page load.
   */
  const setDefaultSite = async (nodeId: string | null) => {
    await apiSetDefaultSite(nodeId);
    setUserState((prev) => {
      if (!prev) return prev;
      const next = { ...prev, default_node_id: nodeId };
      localStorage.setItem('cloud_user', JSON.stringify(next));
      return next;
    });
  };

  /**
   * Unlike the default site, OPTIMISTIC: a card dragged or a view toggled has to stay where it
   * was put, and the board already shows it there. The copy in state is what the next board to
   * mount starts from — so switching between All Sites and a site keeps the arrangement — and
   * the server write is debounced, because Customize can change a dozen things in a minute.
   *
   * Only for a backend that stores layouts (`dashboard_layout` present on the user). A failed
   * save is logged, not surfaced: the browser keeps its own copy, so nothing is lost here.
   */
  const layoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveDashboardLayout = (layout: unknown[] | null) => {
    setUserState((prev) => {
      if (!prev || !('dashboard_layout' in prev)) return prev;
      const next = { ...prev, dashboard_layout: layout };
      localStorage.setItem('cloud_user', JSON.stringify(next));
      return next;
    });
    if (layoutTimer.current) clearTimeout(layoutTimer.current);
    layoutTimer.current = setTimeout(() => {
      layoutTimer.current = null;
      if (!user || !('dashboard_layout' in user)) return;
      apiSetDashboardLayout(layout).catch((err) => console.warn('Could not save the dashboard layout', err));
    }, 800);
  };

  /**
   * Same server-first order as setDefaultSite, and the cached copy is refreshed for the same
   * reason: getStoredUser() seeds the next mount, and a stale copy would re-offer the tour
   * for a moment on every page load until /auth/me answered.
   */
  const markTourSeen = async (tourId: string, version: number, status: 'completed' | 'skipped') => {
    const { tour_state } = await apiSaveTourState(tourId, version, status);
    setUserState((prev) => {
      if (!prev) return prev;
      const next = { ...prev, tour_state };
      localStorage.setItem('cloud_user', JSON.stringify(next));
      return next;
    });
  };

  const logout = () => {
    // A save still waiting would go out with no token — and be the previous user's layout.
    if (layoutTimer.current) clearTimeout(layoutTimer.current);
    layoutTimer.current = null;
    setUserState(null);
    setToken(null);
    apiLogout();
    // The selected site is part of the session, not of the browser: leaving it behind would put
    // the next person to log in here inside someone else's site, with that site's menu.
    clearStoredScope();
  };

  // return (
  //   <AuthContext.Provider value={{ user, token, loading, logout, setUser }}>
  //     {children}
  //   </AuthContext.Provider>
  // );

  const hasPermission = (permission: string): boolean => {
    return checkPermission(user, permission);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, logout, setUser, setDefaultSite, saveDashboardLayout, markTourSeen, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
